import { useCESDK } from './useCESDK';
import supabase from '../utils/supabaseUtils';
import { useCallback } from 'react';

/**
 * Hook specifically for the Editor component
 * This is a wrapper around useCESDK that isolates the editor functionality
 * from other components like the dashboard
 */
export function useEditorCESDK() {
  // Just use the original hook but create a different name/instance
  // This prevents conflicts between different parts of the app
  const ceSDKHook = useCESDK();
  
  const original = ceSDKHook.registerAssetSources;

  /**
   * Optimised version of registerAssetSources
   * – backgrounds are loaded folder-first, images only when the user opens a section.
   * – stickers & athletes are registered exactly once (logic copied from base hook).
   */
  const registerAssetSources = useCallback(async () => {
    console.log('[useEditorCESDK] registerAssetSources invoked');
    // Skip calling the original heavy implementation; we will register the lighter sources ourselves.

    let cesdk = ceSDKHook.cesdk || window.cesdk;
    if (!cesdk?.engine?.asset) {
      // If still not ready, wait briefly for initialization to finish
      await new Promise(r => setTimeout(r, 300));
      cesdk = ceSDKHook.cesdk || window.cesdk;
      if (!cesdk?.engine?.asset) {
        console.warn('CESDK engine still not ready – asset registration aborted');
        return false;
      }
    }

    /* ---------------------------
     * Optimised BACKGROUNDS source
     * --------------------------- */
    try {
      // Remove any existing background sources
      const existingSources = ['supabase-backgrounds', 'baseball-backgrounds', 'basketball-backgrounds', 'football-backgrounds'];
      existingSources.forEach(sourceId => {
        try { cesdk.engine.asset.removeSource(sourceId); } catch (_) {}
      });

      // Helper function to create a simple background source for a specific folder
      const createBackgroundSource = (folderName, sourceId) => {
        let assetsCache = [];
        let loadedPages = new Set();
        let totalCount = null;
        let isLoading = false;
        
        return {
          id: sourceId,
          addAsset: async (asset) => {
            console.log(`🎯 [${sourceId}] addAsset called for:`, asset.id);
            // Instead of immediately applying, show preview modal
            if (window.showBackgroundPreview) {
              console.log(`🎯 [${sourceId}] Calling showBackgroundPreview`);
              window.showBackgroundPreview(sourceId, asset.id);
              return true; // Indicate we handled the add operation
            } else {
              console.error(`🎯 [${sourceId}] showBackgroundPreview not available!`);
              return false; // Fallback to default behavior
            }
          },
          findAssets: async ({ page = 0, perPage = 20 }) => {
            console.log(`[${sourceId}] Loading page ${page}, perPage=${perPage}`);
            
            // Check if this page is already cached
            if (loadedPages.has(page)) {
              const startIndex = page * perPage;
              const endIndex = startIndex + perPage;
              const paginatedAssets = assetsCache.slice(startIndex, endIndex);
              const hasMore = endIndex < (totalCount || assetsCache.length);
              
              console.log(`[${sourceId}] Serving page ${page} from cache: ${paginatedAssets.length} assets`);
              return {
                assets: paginatedAssets,
                total: totalCount || assetsCache.length,
                currentPage: page,
                nextPage: hasMore ? page + 1 : undefined
              };
            }
            
            // Prevent multiple concurrent loads
            if (isLoading) {
              console.log(`[${sourceId}] Already loading, returning empty`);
              return { assets: [], total: 0, currentPage: page };
            }
            
            isLoading = true;
            
            try {
              // Load smaller batch for first page, normal batch for subsequent pages
              const limit = page === 0 ? 8 : perPage;
              const offset = page === 0 ? 0 : (8 + (page - 1) * perPage); // Adjust offset for first small batch
              
              const { data: files, error } = await supabase.storage
                .from('playmakerpics-images')
                .list(`backgrounds/${folderName}`, { 
                  limit: limit, 
                  offset: offset
                });
              
              if (error) throw error;
              
              const imageFiles = files.filter(f => /\.(jpe?g|png|gif)$/i.test(f.name));
              const newAssets = imageFiles.map(file => {
                const { data: urlData } = supabase.storage
                  .from('playmakerpics-images')
                  .getPublicUrl(`backgrounds/${folderName}/${encodeURIComponent(file.name)}`);
                
                return {
                  id: `bg-${folderName}-${file.name}`,
                  label: file.name.split('.')[0],
                  kind: 'image',
                  meta: {
                    uri: urlData.publicUrl,
                    thumbUri: urlData.publicUrl,
                    blockType: '//ly.img.ubq/graphic',
                    fillType: '//ly.img.ubq/fill/image',
                    shapeType: '//ly.img.ubq/shape/rect',
                    width: 1920,
                    height: 1080,
                    mimeType: /png$/i.test(file.name) ? 'image/png' : 'image/jpeg'
                  }
                };
              });
              
              // Add new assets to cache at the correct position
              if (page === 0) {
                assetsCache = newAssets;
                // Get total count for future pagination
                const { data: allFiles } = await supabase.storage
                  .from('playmakerpics-images')
                  .list(`backgrounds/${folderName}`, { limit: 1000 });
                
                if (allFiles) {
                  const totalImages = allFiles.filter(f => /\.(jpe?g|png|gif)$/i.test(f.name)).length;
                  totalCount = totalImages;
                  console.log(`[${sourceId}] Total images in folder: ${totalCount}`);
                }
              } else {
                // Insert assets at correct position in cache
                const insertIndex = 8 + (page - 1) * perPage;
                assetsCache.splice(insertIndex, 0, ...newAssets);
              }
              
              // Mark this page as loaded
              loadedPages.add(page);
              
              const hasMore = page === 0 ? totalCount > 8 : imageFiles.length === perPage;
              console.log(`[${sourceId}] Loaded page ${page}: ${newAssets.length}/${limit} assets, hasMore: ${hasMore}, total: ${totalCount || 'unknown'}`);
              
              // Return the current page from cache
              const startIndex = page * perPage;
              const endIndex = startIndex + perPage;
              const paginatedAssets = assetsCache.slice(startIndex, endIndex);
              
              return {
                assets: paginatedAssets,
                total: totalCount || (hasMore ? assetsCache.length + 50 : assetsCache.length),
                currentPage: page,
                nextPage: hasMore ? page + 1 : undefined
              };
            } catch (error) {
              console.error(`[${sourceId}] Error:`, error);
              return { assets: [], total: 0, currentPage: page };
            } finally {
              isLoading = false;
            }
          }
        };
      };

      // Start with only popular sports for faster loading
      const backgroundSources = [
        { folderName: 'BaseballBG', sourceId: 'baseball-backgrounds', label: 'Baseball' },
        { folderName: 'BasketballBG', sourceId: 'basketball-backgrounds', label: 'Basketball' },
        { folderName: 'FootballBG', sourceId: 'football-backgrounds', label: 'Football' },
        { folderName: 'VolleyballBG', sourceId: 'volleyball-backgrounds', label: 'Volleyball' },
        { folderName: 'ColorLightsBG', sourceId: 'color-lights-backgrounds', label: 'Color Lights' },
        { folderName: 'StadiumLightsBG', sourceId: 'stadium-lights-backgrounds', label: 'Stadium Lights' }
      ];
      
      // Additional sources to load later (after initial UI is ready)
      const additionalSources = [
        { folderName: 'SoftballSmokeBG', sourceId: 'softball-backgrounds', label: 'Softball' },
        { folderName: 'BaseballFlagsBG', sourceId: 'baseball-flags-backgrounds', label: 'Baseball Flags' },
        { folderName: 'CheerleaderConfetti', sourceId: 'cheerleader-backgrounds', label: 'Cheerleader' },
        { folderName: 'ColorDustExplosion', sourceId: 'color-dust-backgrounds', label: 'Color Dust' },
        { folderName: 'FireSmokeBG', sourceId: 'fire-smoke-backgrounds', label: 'Fire & Smoke' }
      ];

      // IMPORTANT: Initialize preview modal FIRST before registering sources
      const createPreviewModal = () => {
        // Create modal container
        const modal = document.createElement('div');
        modal.id = 'background-preview-modal';
        modal.style.cssText = `
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: rgba(0, 0, 0, 0.95);
          display: none;
          justify-content: center;
          align-items: center;
          z-index: 10000;
          backdrop-filter: blur(10px);
          transition: all 0.3s ease;
          opacity: 0;
        `;
        
        modal.innerHTML = `
          <div id="preview-container" style="position: relative; max-width: 90vw; max-height: 90vh; display: flex; align-items: center; transform: scale(0.8); transition: transform 0.3s ease;">
            
            <!-- Left Navigation -->
            <button id="prev-bg" style="
              position: absolute; 
              left: -80px; 
              background: rgba(255,255,255,0.15); 
              border: 2px solid rgba(255,255,255,0.3);
              color: white; 
              padding: 15px 20px; 
              border-radius: 50%; 
              cursor: pointer; 
              font-size: 24px; 
              z-index: 10001;
              transition: all 0.2s ease;
              backdrop-filter: blur(10px);
              width: 60px;
              height: 60px;
              display: flex;
              align-items: center;
              justify-content: center;
              box-shadow: 0 4px 15px rgba(0,0,0,0.3);
            " onmouseover="this.style.background='rgba(255,255,255,0.25)'; this.style.transform='scale(1.1)'" onmouseout="this.style.background='rgba(255,255,255,0.15)'; this.style.transform='scale(1)'">‹</button>
            
            <!-- Main Image Container -->
            <div style="position: relative; display: flex; flex-direction: column; align-items: center;">
              <img id="preview-image" style="
                max-width: 80vw; 
                max-height: 70vh; 
                border-radius: 12px; 
                box-shadow: 0 8px 40px rgba(0,0,0,0.6);
                transition: all 0.3s ease;
                border: 3px solid rgba(255,255,255,0.1);
              " />
              
              <!-- Loading indicator -->
              <div id="loading-indicator" style="
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
                color: white;
                font-size: 18px;
                display: none;
              ">Loading...</div>
            </div>
            
            <!-- Right Navigation -->
            <button id="next-bg" style="
              position: absolute; 
              right: -80px; 
              background: rgba(255,255,255,0.15); 
              border: 2px solid rgba(255,255,255,0.3);
              color: white; 
              padding: 15px 20px; 
              border-radius: 50%; 
              cursor: pointer; 
              font-size: 24px; 
              z-index: 10001;
              transition: all 0.2s ease;
              backdrop-filter: blur(10px);
              width: 60px;
              height: 60px;
              display: flex;
              align-items: center;
              justify-content: center;
              box-shadow: 0 4px 15px rgba(0,0,0,0.3);
            " onmouseover="this.style.background='rgba(255,255,255,0.25)'; this.style.transform='scale(1.1)'" onmouseout="this.style.background='rgba(255,255,255,0.15)'; this.style.transform='scale(1)'">›</button>
            
            <!-- Close Button -->
            <button id="close-preview" style="
              position: absolute; 
              top: -25px; 
              right: -25px; 
              background: rgba(255,255,255,0.15); 
              border: 2px solid rgba(255,255,255,0.3);
              color: white; 
              padding: 12px; 
              border-radius: 50%; 
              cursor: pointer; 
              font-size: 18px;
              z-index: 10001;
              transition: all 0.2s ease;
              backdrop-filter: blur(10px);
              width: 45px;
              height: 45px;
              display: flex;
              align-items: center;
              justify-content: center;
              box-shadow: 0 4px 15px rgba(0,0,0,0.3);
            " onmouseover="this.style.background='rgba(255,50,50,0.3)'; this.style.transform='scale(1.1)'" onmouseout="this.style.background='rgba(255,255,255,0.15)'; this.style.transform='scale(1)'">✕</button>
            
            <!-- Bottom Controls -->
            <div style="
              position: absolute; 
              bottom: -90px; 
              left: 50%; 
              transform: translateX(-50%); 
              text-align: center;
              min-width: 300px;
            ">
              <!-- Image Info -->
              <div id="image-info" style="
                color: rgba(255,255,255,0.9); 
                margin-bottom: 15px; 
                font-size: 16px;
                font-weight: 500;
                text-shadow: 0 2px 4px rgba(0,0,0,0.5);
              "></div>
              
              <!-- Progress Dots -->
              <div id="progress-dots" style="
                display: flex;
                justify-content: center;
                gap: 8px;
                margin-bottom: 20px;
                max-width: 200px;
                overflow: hidden;
                margin-left: auto;
                margin-right: auto;
              "></div>
              
              <!-- Action Buttons -->
              <div style="display: flex; gap: 15px; justify-content: center;">
                <button id="apply-bg" style="
                  background: linear-gradient(135deg, #007bff, #0056b3); 
                  color: white; 
                  border: none; 
                  padding: 12px 25px; 
                  border-radius: 8px; 
                  cursor: pointer; 
                  font-weight: bold;
                  font-size: 16px;
                  transition: all 0.2s ease;
                  box-shadow: 0 4px 15px rgba(0,123,255,0.3);
                " onmouseover="this.style.transform='translateY(-2px)'; this.style.boxShadow='0 6px 20px rgba(0,123,255,0.4)'" onmouseout="this.style.transform='translateY(0)'; this.style.boxShadow='0 4px 15px rgba(0,123,255,0.3)'">Apply Background</button>
                
                <button id="fullscreen-bg" style="
                  background: rgba(255,255,255,0.15); 
                  color: white; 
                  border: 2px solid rgba(255,255,255,0.3);
                  padding: 12px 20px; 
                  border-radius: 8px; 
                  cursor: pointer; 
                  font-weight: 500;
                  font-size: 16px;
                  transition: all 0.2s ease;
                  backdrop-filter: blur(10px);
                " onmouseover="this.style.background='rgba(255,255,255,0.25)'; this.style.transform='translateY(-2px)'" onmouseout="this.style.background='rgba(255,255,255,0.15)'; this.style.transform='translateY(0)'">View Full Size</button>
              </div>
            </div>
          </div>
        `;
        
        document.body.appendChild(modal);
        
        let currentAssets = [];
        let currentIndex = 0;
        
        const createProgressDots = () => {
          const dotsContainer = document.getElementById('progress-dots');
          dotsContainer.innerHTML = '';
          
          const maxDots = 10; // Show max 10 dots
          const totalAssets = currentAssets.length;
          
          if (totalAssets <= maxDots) {
            // Show all dots
            for (let i = 0; i < totalAssets; i++) {
              const dot = document.createElement('div');
              dot.style.cssText = `
                width: 8px;
                height: 8px;
                border-radius: 50%;
                background: ${i === currentIndex ? 'white' : 'rgba(255,255,255,0.3)'};
                transition: all 0.2s ease;
                cursor: pointer;
              `;
              dot.onclick = () => {
                currentIndex = i;
                updatePreview();
              };
              dotsContainer.appendChild(dot);
            }
          } else {
            // Show condensed view with current position
            const text = document.createElement('div');
            text.style.cssText = `
              color: rgba(255,255,255,0.7);
              font-size: 14px;
              font-weight: 500;
            `;
            text.textContent = `${currentIndex + 1} of ${totalAssets}`;
            dotsContainer.appendChild(text);
          }
        };
        
        const updatePreview = () => {
          if (currentAssets.length === 0) return;
          
          const asset = currentAssets[currentIndex];
          const img = document.getElementById('preview-image');
          const info = document.getElementById('image-info');
          const loading = document.getElementById('loading-indicator');
          
          // Show loading
          loading.style.display = 'block';
          img.style.opacity = '0.5';
          
          // Update image
          img.onload = () => {
            loading.style.display = 'none';
            img.style.opacity = '1';
          };
          img.src = asset.meta.uri;
          info.textContent = asset.label;
          
          // Update navigation buttons
          const prevBtn = document.getElementById('prev-bg');
          const nextBtn = document.getElementById('next-bg');
          
          prevBtn.style.opacity = currentIndex > 0 ? '1' : '0.4';
          prevBtn.style.pointerEvents = currentIndex > 0 ? 'auto' : 'none';
          nextBtn.style.opacity = currentIndex < currentAssets.length - 1 ? '1' : '0.4';
          nextBtn.style.pointerEvents = currentIndex < currentAssets.length - 1 ? 'auto' : 'none';
          
          // Update progress dots
          createProgressDots();
        };
        
        // Navigation handlers
        document.getElementById('prev-bg').onclick = () => {
          if (currentIndex > 0) {
            currentIndex--;
            updatePreview();
          }
        };
        
        document.getElementById('next-bg').onclick = () => {
          if (currentIndex < currentAssets.length - 1) {
            currentIndex++;
            updatePreview();
          }
        };
        
        document.getElementById('close-preview').onclick = () => {
          modal.style.opacity = '0';
          document.getElementById('preview-container').style.transform = 'scale(0.8)';
          setTimeout(() => {
            modal.style.display = 'none';
          }, 300);
        };
        
        document.getElementById('fullscreen-bg').onclick = () => {
          const asset = currentAssets[currentIndex];
          if (asset) {
            window.open(asset.meta.uri, '_blank');
          }
        };
        
        document.getElementById('apply-bg').onclick = () => {
          if (currentAssets.length > 0) {
            const asset = currentAssets[currentIndex];
            // Apply background directly using CESDK block API to bypass interception issues
            try {
              console.log('🎯 Applying background directly, bypassing all interception:', asset.id);
              
              // Get current CESDK instance and page
              const currentCesdk = cesdk || ceSDKHook.cesdk || window.cesdk;
              const page = currentCesdk.engine.scene.getCurrentPage();
              if (!page) {
                throw new Error('No current page found');
              }
              
              // Create a new image block for the background
              const block = currentCesdk.engine.block.create('graphic');
              
              // Set it as a rectangle shape
              const rectShape = currentCesdk.engine.block.createShape('rect');
              currentCesdk.engine.block.setShape(block, rectShape);
              
              // Create image fill and set the image using the reliable old method
              const imageFill = currentCesdk.engine.block.createFill('image');
              
              // Use the modern setSourceSet API instead of deprecated imageFileURI
              // Reference: https://img.ly/docs/cesdk/react/import-media/source-sets-5679c8/
              currentCesdk.engine.block.setSourceSet(imageFill, 'fill/image/sourceSet', [{
                uri: asset.meta.uri,
                width: parseInt(asset.meta.width) || 1920,
                height: parseInt(asset.meta.height) || 1080
              }]);
              
              currentCesdk.engine.block.setFill(block, imageFill);
              
              // Get page dimensions and set block size to match (CESDK 1.52.0 compatible)
              try {
                // Try to get page dimensions - different methods for different CESDK versions
                let pageWidth = 1920;  // Default fallback
                let pageHeight = 1080; // Default fallback
                
                // Method 1: Try getting from page block properties
                try {
                  pageWidth = currentCesdk.engine.block.getFloat(page, 'page/width');
                  pageHeight = currentCesdk.engine.block.getFloat(page, 'page/height');
                } catch (e) {
                  // Method 2: Try scene API
                  try {
                    pageWidth = currentCesdk.engine.scene.getWidth();
                    pageHeight = currentCesdk.engine.scene.getHeight();
                  } catch (e2) {
                    // Method 3: Use block frame properties
                    try {
                      pageWidth = currentCesdk.engine.block.getFrameWidth ? 
                        currentCesdk.engine.block.getFrameWidth(page) : 1920;
                      pageHeight = currentCesdk.engine.block.getFrameHeight ? 
                        currentCesdk.engine.block.getFrameHeight(page) : 1080;
                    } catch (e3) {
                      console.warn('Could not get page dimensions, using defaults:', e3);
                    }
                  }
                }
                
                console.log('📐 Page dimensions:', pageWidth, 'x', pageHeight);
                currentCesdk.engine.block.setWidth(block, pageWidth);
                currentCesdk.engine.block.setHeight(block, pageHeight);
              } catch (dimensionError) {
                console.warn('Error getting dimensions, using defaults:', dimensionError);
                currentCesdk.engine.block.setWidth(block, 1920);
                currentCesdk.engine.block.setHeight(block, 1080);
              }
              
              // Position at origin
              currentCesdk.engine.block.setPositionX(block, 0);
              currentCesdk.engine.block.setPositionY(block, 0);
              
              // Add to page and send to back so it's behind other elements
              currentCesdk.engine.block.appendChild(page, block);
              currentCesdk.engine.block.sendToBack(block);
              
              console.log('✅ Background applied successfully using direct block creation!');
              
              // Close modal with success animation
              document.getElementById('apply-bg').textContent = '✓ Applied!';
              document.getElementById('apply-bg').style.background = 'linear-gradient(135deg, #28a745, #20c997)';
              setTimeout(() => {
                document.getElementById('close-preview').click();
              }, 800);
            } catch (error) {
              console.error('Error applying background:', error);
              
              // Show error message to user
              document.getElementById('apply-bg').textContent = '❌ Error';
              document.getElementById('apply-bg').style.background = 'linear-gradient(135deg, #dc3545, #c82333)';
              setTimeout(() => {
                document.getElementById('apply-bg').textContent = 'Apply Background';
                document.getElementById('apply-bg').style.background = 'linear-gradient(135deg, #007bff, #0056b3)';
              }, 2000);
              
              document.getElementById('close-preview').click();
            }
          }
        };
        
        // Close on background click
        modal.onclick = (e) => {
          if (e.target === modal) {
            document.getElementById('close-preview').click();
          }
        };
        
        // Keyboard navigation
        document.addEventListener('keydown', (e) => {
          if (modal.style.display === 'flex') {
            if (e.key === 'ArrowLeft') {
              e.preventDefault();
              document.getElementById('prev-bg').click();
            }
            if (e.key === 'ArrowRight') {
              e.preventDefault();
              document.getElementById('next-bg').click();
            }
            if (e.key === 'Escape') {
              e.preventDefault();
              document.getElementById('close-preview').click();
            }
            if (e.key === 'Enter') {
              e.preventDefault();
              document.getElementById('apply-bg').click();
            }
            if (e.key === ' ') { // Spacebar for fullscreen
              e.preventDefault();
              document.getElementById('fullscreen-bg').click();
            }
          }
        });
        
        // Touch/swipe support for mobile
        let touchStartX = 0;
        let touchStartY = 0;
        const img = document.getElementById('preview-image');
        
        img.addEventListener('touchstart', (e) => {
          touchStartX = e.touches[0].clientX;
          touchStartY = e.touches[0].clientY;
        }, { passive: true });
        
        img.addEventListener('touchend', (e) => {
          if (!touchStartX || !touchStartY) return;
          
          const touchEndX = e.changedTouches[0].clientX;
          const touchEndY = e.changedTouches[0].clientY;
          
          const deltaX = touchStartX - touchEndX;
          const deltaY = touchStartY - touchEndY;
          
          // Require minimum swipe distance
          const minSwipeDistance = 50;
          
          // Horizontal swipe (left/right navigation)
          if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > minSwipeDistance) {
            if (deltaX > 0) {
              // Swiped left - next image
              document.getElementById('next-bg').click();
            } else {
              // Swiped right - previous image
              document.getElementById('prev-bg').click();
            }
          }
          
          // Reset touch coordinates
          touchStartX = 0;
          touchStartY = 0;
        }, { passive: true });
        
        return {
          show: (assets, startIndex = 0) => {
            currentAssets = assets;
            currentIndex = startIndex;
            
            // Reset apply button state
            const applyBtn = document.getElementById('apply-bg');
            applyBtn.textContent = 'Apply Background';
            applyBtn.style.background = 'linear-gradient(135deg, #007bff, #0056b3)';
            
            updatePreview();
            
            // Show modal with smooth animation
            modal.style.display = 'flex';
            // Trigger reflow before animating
            modal.offsetHeight;
            modal.style.opacity = '1';
            document.getElementById('preview-container').style.transform = 'scale(1)';
          }
        };
      };
      
      // Initialize preview modal
      const previewModal = createPreviewModal();
      
      // Override asset application to show preview instead
      window.showBackgroundPreview = async (sourceId, clickedAssetId) => {
        try {
          // Get the correct CESDK instance
          const currentCesdk = cesdk || ceSDKHook.cesdk || window.cesdk;
          
          if (!currentCesdk?.engine?.asset) {
            console.error('🎯 CESDK engine not available for preview');
            return;
          }
          
          // Show loading state
          const loadingModal = document.createElement('div');
          loadingModal.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: rgba(0, 0, 0, 0.8);
            display: flex;
            justify-content: center;
            align-items: center;
            z-index: 9999;
            backdrop-filter: blur(5px);
          `;
          loadingModal.innerHTML = `
            <div style="text-align: center; color: white;">
              <div style="width: 40px; height: 40px; border: 3px solid rgba(255,255,255,0.3); border-top: 3px solid white; border-radius: 50%; animation: spin 1s linear infinite; margin: 0 auto 15px;"></div>
              <div style="font-size: 16px;">Loading backgrounds...</div>
            </div>
            <style>
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            </style>
          `;
          document.body.appendChild(loadingModal);
          
          // Use findAllSources for CESDK 1.52.0 compatibility
          const allSources = currentCesdk.engine.asset.findAllSources();
          
          console.log(`[Preview] Looking for source "${sourceId}" in ${allSources.length} sources`);
          console.log(`[Preview] Available sources:`, allSources);
          
          // findAllSources returns source IDs as strings, not objects
          const sourceExists = allSources.includes(sourceId);
          
          if (sourceExists) {
            console.log(`[Preview] Found source "${sourceId}"`);
            
            // We need to get the actual source object to call findAssets
            // Since we registered the sources ourselves, let's try to find it from our registered sources
            let source = null;
            
            // Try to find the source in a different way - check if there's a way to get source by ID
            try {
              // In CESDK 1.52.0, findAssets takes sourceId string and options object
              const result = await currentCesdk.engine.asset.findAssets(sourceId, {
                page: 0,
                perPage: 30
              });
              
              if (result && result.assets && result.assets.length > 0) {
                console.log(`[Preview] Found ${result.assets.length} assets using findAssets`);
                
                // Remove loading modal
                document.body.removeChild(loadingModal);
                
                // Find the clicked asset index
                const startIndex = result.assets.findIndex(asset => asset.id === clickedAssetId);
                
                // Show preview modal
                previewModal.show(result.assets, Math.max(0, startIndex));
                return;
              } else {
                console.warn(`[Preview] No assets returned from source "${sourceId}". Total: ${result?.total || 0}`);
              }
            } catch (findAssetsError) {
              console.warn(`[Preview] findAssets method failed:`, findAssetsError);
            }
            
            // If findAssets didn't work, we need another approach
            // The source exists but we can't access it directly - show error
            document.body.removeChild(loadingModal);
            console.error(`[Preview] Source "${sourceId}" found but cannot access assets`);
            
            const errorDiv = document.createElement('div');
            errorDiv.style.cssText = `
              position: fixed;
              top: 50%;
              left: 50%;
              transform: translate(-50%, -50%);
              background: rgba(255, 69, 58, 0.9);
              color: white;
              padding: 20px;
              border-radius: 8px;
              z-index: 10000;
              font-size: 16px;
              backdrop-filter: blur(10px);
            `;
            errorDiv.textContent = `Cannot access assets from source "${sourceId}"`;
            document.body.appendChild(errorDiv);
            setTimeout(() => document.body.removeChild(errorDiv), 3000);
            
          } else {
            // Remove loading modal
            document.body.removeChild(loadingModal);
            console.error(`[Preview] Source ${sourceId} not found in CESDK`);
            console.error(`[Preview] Available sources:`, allSources);
            
            // Show error message
            const errorDiv = document.createElement('div');
            errorDiv.style.cssText = `
              position: fixed;
              top: 50%;
              left: 50%;
              transform: translate(-50%, -50%);
              background: rgba(255, 69, 58, 0.9);
              color: white;
              padding: 20px;
              border-radius: 8px;
              z-index: 10000;
              font-size: 16px;
              backdrop-filter: blur(10px);
            `;
            errorDiv.textContent = `Background source "${sourceId}" not found`;
            document.body.appendChild(errorDiv);
            setTimeout(() => document.body.removeChild(errorDiv), 3000);
          }
        } catch (error) {
          // Remove loading modal if it exists
          const loadingModals = document.querySelectorAll('[style*="Loading backgrounds"]');
          loadingModals.forEach(modal => {
            try { document.body.removeChild(modal.parentElement); } catch (e) {}
          });
          
          console.error('Error showing background preview:', error);
          
          // Show error message
          const errorDiv = document.createElement('div');
          errorDiv.style.cssText = `
            position: fixed;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            background: rgba(255, 69, 58, 0.9);
            color: white;
            padding: 20px;
            border-radius: 8px;
            z-index: 10000;
            font-size: 16px;
            backdrop-filter: blur(10px);
          `;
          errorDiv.textContent = `Error loading preview: ${error.message}`;
          document.body.appendChild(errorDiv);
          setTimeout(() => document.body.removeChild(errorDiv), 3000);
        }
      };

      // NOW register the background sources (after preview function is ready)
      console.log('🚀 Registering background sources...');
      
      // Register priority background sources first
      backgroundSources.forEach(({ folderName, sourceId }) => {
        const source = createBackgroundSource(folderName, sourceId);
        cesdk.engine.asset.addSource(source);
        
        // Also add to the dock library entries
        try {
          if (cesdk.ui && cesdk.ui.dock) {
            cesdk.ui.dock.addLibraryEntry(sourceId, {
              id: sourceId,
              sourceIds: [sourceId],
              previewLength: 3,
              gridColumns: 3,
              gridItemAspectRatio: 1
            });
          }
        } catch (e) {
          console.warn(`Could not add ${sourceId} to dock:`, e);
        }
        
        console.log(`✔ Registered ${sourceId}`);
      });
      
      // Load additional sources after a delay to avoid blocking
      setTimeout(() => {
        console.log('Loading additional background sources...');
        additionalSources.forEach(({ folderName, sourceId }) => {
          const source = createBackgroundSource(folderName, sourceId);
          cesdk.engine.asset.addSource(source);
          console.log(`✔ Registered ${sourceId}`);
        });
        console.log(`✔ Loaded ${additionalSources.length} additional background sources`);
      }, 2000); // 2 second delay
      
      console.log(`✔ Registered ${backgroundSources.length} priority background sources (${additionalSources.length} more loading...)`);

      // DIAGNOSTIC: Check what sources are available after our registration
      setTimeout(() => {
        try {
          const allSources = cesdk.engine.asset.findAllSources();
          console.log('🔍 All available sources after registration:', allSources);
          
          // Check specifically for default CESDK image sources
          const defaultImageSources = allSources.filter(s => 
            s.includes('ly.img') || s.includes('unsplash') || s.includes('image') || s.includes('stock')
          );
          console.log('🖼️ Default image sources found:', defaultImageSources);
          
          // If no default image sources, this might be the issue
          if (defaultImageSources.length === 0) {
            console.warn('⚠️ No default CESDK image sources found - this might be why Images dock is empty');
          }
        } catch (e) {
          console.error('Error checking sources:', e);
        }
      }, 500);

      // CRITICAL: Global asset interception to show preview modal instead of direct application
      setTimeout(() => {
        const currentCesdk = cesdk || ceSDKHook.cesdk || window.cesdk;
        if (currentCesdk && currentCesdk.engine && currentCesdk.engine.asset) {
          console.log('🎯 Setting up global asset interception...');
          
          const originalApply = currentCesdk.engine.asset.apply;
          currentCesdk.engine.asset.apply = function(assetId, ...args) {
            console.log('🔍 Asset apply intercepted:', assetId, 'args:', args);
            
            // ONLY intercept our custom background sources - let everything else work normally
            const backgroundSources = [
              'baseball-backgrounds', 'basketball-backgrounds', 'football-backgrounds',
              'volleyball-backgrounds', 'color-lights-backgrounds', 'stadium-lights-backgrounds',
              'softball-backgrounds', 'baseball-flags-backgrounds', 'cheerleader-backgrounds',
              'color-dust-backgrounds', 'fire-smoke-backgrounds'
            ];
            
            // Only intercept if it's specifically our background sources or assets
            if (backgroundSources.includes(assetId)) {
              console.log('🎯 Background source detected, showing preview...');
              window.showBackgroundPreview(assetId, 'first-asset');
              return; // Prevent default application
            }
            
            // Only intercept individual assets that are specifically our background assets (bg-...)
            if (assetId && typeof assetId === 'string' && assetId.startsWith('bg-')) {
              console.log('🎯 Individual background asset detected, showing preview...');
              
              // Extract source ID from asset ID pattern: bg-{FolderName}-{filename}
              const match = assetId.match(/bg-(\w+)-/);
              if (match) {
                const folderName = match[1];
                
                const folderToSourceMap = {
                  'BaseballBG': 'baseball-backgrounds',
                  'BasketballBG': 'basketball-backgrounds', 
                  'FootballBG': 'football-backgrounds',
                  'VolleyballBG': 'volleyball-backgrounds',
                  'ColorLightsBG': 'color-lights-backgrounds',
                  'StadiumLightsBG': 'stadium-lights-backgrounds',
                  'SoftballSmokeBG': 'softball-backgrounds',
                  'BaseballFlagsBG': 'baseball-flags-backgrounds',
                  'CheerleaderConfetti': 'cheerleader-backgrounds',
                  'ColorDustExplosion': 'color-dust-backgrounds',
                  'FireSmokeBG': 'fire-smoke-backgrounds'
                };
                
                const sourceId = folderToSourceMap[folderName];
                if (sourceId) {
                  console.log('🎯 Showing preview for source:', sourceId);
                  window.showBackgroundPreview(sourceId, assetId);
                  return;
                }
              }
            }
            
            // For ALL other assets (including default CESDK images), use original behavior
            console.log('📋 Non-background asset, using original apply:', assetId);
            return originalApply.call(this, assetId, ...args);
          };
          
          // Also set it on window.cesdk for global access
          if (window.cesdk && window.cesdk.engine && window.cesdk.engine.asset) {
            window.cesdk.engine.asset.apply = currentCesdk.engine.asset.apply;
          }
          
          console.log('✅ Global asset interception enabled automatically!');
        } else {
          console.warn('⚠️ CESDK not ready for asset interception, will retry...');
          // Retry after another delay
          setTimeout(() => {
            const retryCesdk = cesdk || ceSDKHook.cesdk || window.cesdk;
            if (retryCesdk && retryCesdk.engine && retryCesdk.engine.asset) {
              const originalApply = retryCesdk.engine.asset.apply;
              retryCesdk.engine.asset.apply = function(assetId) {
                console.log('🔍 Asset apply intercepted (retry):', assetId);
                
                const backgroundSources = [
                  'baseball-backgrounds', 'basketball-backgrounds', 'football-backgrounds',
                  'volleyball-backgrounds', 'color-lights-backgrounds', 'stadium-lights-backgrounds',
                  'softball-backgrounds', 'baseball-flags-backgrounds', 'cheerleader-backgrounds',
                  'color-dust-backgrounds', 'fire-smoke-backgrounds'
                ];
                
                if (backgroundSources.includes(assetId)) {
                  console.log('🎯 Background source detected (retry), showing preview...');
                  window.showBackgroundPreview(assetId, 'first-asset');
                  return;
                }
                
                if (assetId && assetId.includes('bg-')) {
                  console.log('🎯 Individual background asset detected (retry), showing preview...');
                  
                  const match = assetId.match(/bg-(\w+)-/);
                  if (match) {
                    const folderName = match[1];
                    const folderToSourceMap = {
                      'BaseballBG': 'baseball-backgrounds',
                      'BasketballBG': 'basketball-backgrounds', 
                      'FootballBG': 'football-backgrounds',
                      'VolleyballBG': 'volleyball-backgrounds',
                      'ColorLightsBG': 'color-lights-backgrounds',
                      'StadiumLightsBG': 'stadium-lights-backgrounds',
                      'SoftballSmokeBG': 'softball-backgrounds',
                      'BaseballFlagsBG': 'baseball-flags-backgrounds',
                      'CheerleaderConfetti': 'cheerleader-backgrounds',
                      'ColorDustExplosion': 'color-dust-backgrounds',
                      'FireSmokeBG': 'fire-smoke-backgrounds'
                    };
                    
                    const sourceId = folderToSourceMap[folderName];
                    if (sourceId) {
                      console.log('🎯 Showing preview for source (retry):', sourceId);
                      window.showBackgroundPreview(sourceId, assetId);
                      return;
                    }
                  }
                }
                
                return originalApply.apply(this, arguments);
              };
              
              // Also set it on window.cesdk for global access
              if (window.cesdk && window.cesdk.engine && window.cesdk.engine.asset) {
                window.cesdk.engine.asset.apply = retryCesdk.engine.asset.apply;
              }
              
              console.log('✅ Global asset interception enabled (retry successful)!');
            } else {
              console.error('❌ CESDK still not ready after retry');
            }
          }, 1000);
        }
      }, 100); // Short delay to ensure CESDK is fully initialized

      // Expose quick helper for manual debugging in the browser console
      try {
        // Test modal function
        window.testBackgroundModal = () => {
          console.log('🧪 Testing background modal...');
          if (window.showBackgroundPreview) {
            console.log('🧪 showBackgroundPreview function exists, testing...');
            window.showBackgroundPreview('test-source', 'test-bg-1');
          } else {
            console.error('🧪 showBackgroundPreview function not available!');
          }
        };
        
        // Direct modal test that bypasses CESDK
        window.testModalDirect = () => {
          console.log('🧪 Testing modal directly...');
          const modal = document.querySelector('#background-preview-modal');
          if (modal) {
            console.log('🧪 Modal found, showing directly...');
            
            // Create test assets
            const testAssets = [
              {
                id: 'test-bg-1',
                label: 'Test Background 1',
                meta: { uri: 'https://picsum.photos/800/600?random=1' }
              },
              {
                id: 'test-bg-2', 
                label: 'Test Background 2',
                meta: { uri: 'https://picsum.photos/800/600?random=2' }
              }
            ];
            
            // Manually trigger modal show
            try {
              // Set up the modal state manually
              modal.style.display = 'flex';
              modal.offsetHeight; // trigger reflow
              modal.style.opacity = '1';
              document.getElementById('preview-container').style.transform = 'scale(1)';
              
              // Set the first image
              const img = document.getElementById('preview-image');
              const info = document.getElementById('image-info');
              img.src = testAssets[0].meta.uri;
              info.textContent = testAssets[0].label;
              
              console.log('🧪 Modal should now be visible!');
            } catch (e) {
              console.error('🧪 Error showing modal directly:', e);
            }
          } else {
            console.error('🧪 Modal element not found in DOM!');
          }
        };
        
        console.log('%c🧪 Call window.testBackgroundModal() to test the modal', 'color: #00ff00');
        console.log('%c🧪 Call window.testModalDirect() to test modal UI directly', 'color: #00ff00');
        console.log('%cℹ️  Call window.debugBackgrounds() to inspect background sources', 'color: #00bfff');
        console.log('%c🎯 Check console for debug messages when clicking backgrounds', 'color: #ff9500');
      } catch (_) {/* ignore */}
    } catch (err) {
      console.error('Failed to register optimised backgrounds source', err);
    }

    /* ---------------------------
     * Stickers (unchanged)
     * --------------------------- */
    try {
      // Check if sticker source already exists (CESDK 1.52.0 compatibility)
      const existingStickerSource = cesdk.engine.asset.findAllSources().find(s => s.id === 'local-imgly-stickers');
      if (!existingStickerSource) {
        cesdk.engine.asset.addSource({
          id: 'local-imgly-stickers',
          findAssets: async () => {
            const stickerFiles = ['fire.png', 'lightning.png', 'goal.png'];
            const base = `${window.location.origin}/cesdk-assets/imglyAssets/ly.img.sticker`;
            const assets = stickerFiles.map(f => ({
              id: `sticker-${f}`,
              kind: 'sticker',
              meta: {
                uri: `${base}/${f}?t=${Date.now()}`,
                thumbUri: `${base}/${f}?t=${Date.now()}`,
                blockType: '//ly.img.ubq/graphic',
                fillType: '//ly.img.ubq/fill/image',
                shapeType: '//ly.img.ubq/shape/rect',
                width: 512,
                height: 512,
                mimeType: 'image/png'
              }
            }));
            return { assets, total: assets.length, currentPage: 0 };
          }
        });
      }
    } catch (e) { console.warn('Sticker source error', e); }

    /* ---------------------------
     * Athletes (copied from base implementation)
     * --------------------------- */
    try {
      // Check if athlete source already exists (CESDK 1.52.0 compatibility)
      const existingAthleteSource = cesdk.engine.asset.findAllSources().find(s => s.id === 'supabase-athlete-images');
      if (!existingAthleteSource) {
        cesdk.engine.asset.addSource({
          id: 'supabase-athlete-images',
          findAssets: async () => {
            try {
              const { data, error } = await supabase.storage
                .from('playmakerpics-images')
                .list('athletes', { limit: 100, offset: 0 });
              if (error) throw error;

              const files = data.filter(f => /\.(jpe?g|png|gif)$/i.test(f.name));
              const assets = files.map((file) => {
                const { data: urlData } = supabase.storage
                  .from('playmakerpics-images')
                  .getPublicUrl(`athletes/${encodeURIComponent(file.name)}`);
                return {
                  id: `athlete-${file.name}`,
                  label: file.name.split('.')[0],
                  kind: 'image',
                  meta: {
                    uri: `${urlData.publicUrl}?t=${Date.now()}`,
                    thumbUri: `${urlData.publicUrl}?t=${Date.now()}`,
                    category: 'Athletes',
                    blockType: '//ly.img.ubq/graphic',
                    fillType: '//ly.img.ubq/fill/image',
                    shapeType: '//ly.img.ubq/shape/rect'
                  }
                };
              });
              return { assets, total: assets.length, currentPage: 0 };
            } catch (err) {
              console.error('Athlete images error', err);
              return { assets: [], total: 0 };
            }
          }
        });
      }
    } catch (e) { console.warn('Athlete source error', e); }

    /* ---------------------------
     * User Uploads (new)
     * --------------------------- */
    try {
      const existingUserSource = cesdk.engine.asset.findAllSources().find(s => s.id === 'user-uploads');
      if (!existingUserSource) {
        cesdk.engine.asset.addSource({
          id: 'user-uploads',
          findAssets: async ({ page = 0, perPage = 25 } = {}) => {
            try {
              // Get current user
              const { data: { user } } = await supabase.auth.getUser();
              if (!user) {
                return { assets: [], total: 0, currentPage: page };
              }

              // List files in the user's upload folder with pagination
              const prefix = `user_uploads/${user.id}`;
              const { data: files, error } = await supabase.storage
                .from('playmakerpics-images')
                .list(prefix, { limit: perPage, offset: page * perPage });

              if (error) throw error;

              const imageFiles = (files || []).filter(f => /\.(jpe?g|png|gif)$/i.test(f.name));

              // Build asset objects
              const assets = imageFiles.map(file => {
                const { data: urlData } = supabase.storage
                  .from('playmakerpics-images')
                  .getPublicUrl(`${prefix}/${encodeURIComponent(file.name)}`);

                const uri = `${urlData.publicUrl}?t=${Date.now()}`;

                return {
                  id: `upload-${file.name}`,
                  label: file.name.split('.')[0],
                  kind: 'image',
                  meta: {
                    uri,
                    thumbUri: uri,
                    blockType: '//ly.img.ubq/graphic',
                    fillType: '//ly.img.ubq/fill/image',
                    shapeType: '//ly.img.ubq/shape/rect',
                    width: 1200,
                    height: 800,
                    mimeType: /png$/i.test(file.name) ? 'image/png' : 'image/jpeg'
                  }
                };
              });

              const hasMore = imageFiles.length === perPage;
              return {
                assets,
                total: assets.length + (hasMore ? perPage : 0), // approximate
                currentPage: page,
                nextPage: hasMore ? page + 1 : undefined
              };
            } catch (e) {
              console.error('Error fetching user uploads:', e);
              return { assets: [], total: 0, currentPage: page };
            }
          }
        });
        console.log('✔ Registered user uploads source');
      }
    } catch (e) {
      console.warn('Could not register user uploads source:', e);
    }

    /* ---------------------------
     * Ensure built-in upload source
     * --------------------------- */
    try {
      const uploadSourceExists = cesdk.engine.asset.findAllSources().includes('ly.img.upload');
      if (!uploadSourceExists) {
        cesdk.engine.asset.addLocalSource('ly.img.upload');
        console.log('✔ Registered ly.img.upload local source');
      }
    } catch (e) {
      console.warn('Could not register ly.img.upload source:', e);
    }

    return true;
  }, [original, ceSDKHook.cesdk]);

  // Create Instagram Story blank scene (1080x1920) - following CESDK 1.52.0 documentation
  const createInstagramStoryScene = useCallback(async () => {
    const cesdk = ceSDKHook.cesdk;
    if (!cesdk || !cesdk.engine) {
      throw new Error('CESDK not initialized');
    }

    try {
      console.log("Creating Instagram Story blank scene (1080x1920)...");
      
      // Create empty scene using CESDK 1.52.0 approach from documentation
      // Reference: https://img.ly/docs/cesdk/react/open-the-editor/blank-canvas-18ff05/
      const scene = await cesdk.engine.scene.create();
      
      // Create page with Instagram Story dimensions (1080x1920)
      const page = cesdk.engine.block.create('page');
      cesdk.engine.block.setWidth(page, 1080);
      cesdk.engine.block.setHeight(page, 1920);
      
      // Append page to scene - this is all we need for a clean blank canvas
      cesdk.engine.block.appendChild(scene, page);
      
      console.log("✅ Instagram Story blank scene created successfully (1080x1920)");
      return scene;
    } catch (err) {
      console.error('Error creating Instagram Story blank scene:', err);
      throw err;
    }
  }, [ceSDKHook.cesdk]);

  // SECOND_EDIT: expose the custom function instead of the original + new scene creation
  return {
    ...ceSDKHook,
    registerAssetSources,
    createInstagramStoryScene
  };
} 